import {
  findMaterial,
  sameMaterialRef,
  truncateMaterialLabel,
} from '../../../deck/materials';
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownLabel,
  DropdownTrigger,
} from '../../components/Dropdown';
import { DropdownSelector } from '../../components/DropdownSelector';
import { Plus } from '../../icons';
import { restyleKey } from '../../utils/restyleKey';
import type { DeckMaterialsSwitcherMenuProps } from './DeckMaterials.types';
import { DECK_MATERIAL_ICONS } from './icons';

/**
 * WHICH ONE of a kind a surface is showing, as a chip that opens the rest —
 * Figma `74:27055` (Multi-Source-Set, the phone's "Deck - Doc" frame).
 *
 * It stands where the tab bar stands on web and iPad: a phone has no room for
 * a strip of tabs over a page, so ONE chip names what is open and lists its
 * siblings, and the pane's X (not a tab's) is the way back to All Sources.
 *
 * ONE menu for all three kinds. The phone hosts documents, notes and maps on
 * three separate tabs, and each needs the same question answered — a deck
 * holds several of each, and without this there is no way to reach the second
 * one. Three copies of this chip is how the three tabs would come to list
 * their items in three different orders under three different names, so the
 * kind is a PROP: the rows are the deck's materials of that kind in the grid's
 * order, each wearing the material icon table's glyph, and the one in force
 * carries the DS radio so the list also says where you are.
 *
 * A material still on its way in is listed disabled with its reason, never
 * dropped: it is in the grid the user just left.
 *
 * `addItem` leads the list with "make another one" — the way to a new note,
 * map or source from the surface that shows the current one.
 *
 * The chip is the DS `DropdownSelector` at its smallest band and the panel is
 * the DS `Dropdown`, so the open state, the chevron flip and the native
 * anchored panel are the design system's, not this block's.
 */
export function DeckMaterialsSwitcherMenu({
  items,
  state,
  kind = 'source',
  selected,
  onSelect,
  addItem,
  accessibilityLabel,
  label,
  disabledHint,
  maxLabelChars = 14,
  side = 'bottom',
  align = 'start',
  size = 's',
  onOpenChange,
  className,
  testID,
}: DeckMaterialsSwitcherMenuProps) {
  const listed = items.filter((item) => item.ref.type === kind);
  // Which row is current. `selected` wins when the host has its own answer —
  // a phone's Notes tab is not the pane's open material, so its surface
  // resolves which note it is showing and hands it here, or the chip would
  // name one note while the editor below rendered another.
  const current = selected
    ? findMaterial(listed, selected)
    : state.view === 'item'
      ? findMaterial(listed, state.ref)
      : undefined;
  // With nothing of this kind open, the chip still names the first — that is
  // what the surface under it falls back to showing.
  const shown = current ?? listed[0];
  const ShownIcon = shown
    ? DECK_MATERIAL_ICONS[shown.kind]
    : DECK_MATERIAL_ICONS.document;
  const isCurrent = (ref: (typeof listed)[number]['ref']) =>
    selected
      ? sameMaterialRef(selected, ref)
      : state.view === 'item' && sameMaterialRef(state.ref, ref);

  return (
    <Dropdown size={size} onOpenChange={onOpenChange} testID={testID}>
      <DropdownTrigger className={className}>
        {({ open, onPress }) => (
          <DropdownSelector
            size="s"
            variant="secondary"
            open={open}
            onPress={onPress}
            leftIcon={ShownIcon}
            accessibilityLabel={accessibilityLabel}
            testID={testID ? `${testID}-trigger` : undefined}>
            {shown ? truncateMaterialLabel(shown.label, maxLabelChars) : ''}
          </DropdownSelector>
        )}
      </DropdownTrigger>
      <DropdownContent side={side} align={align} minWidth={220}>
        {label ? <DropdownLabel>{label}</DropdownLabel> : null}
        {addItem ? (
          <DropdownItem
            icon={Plus}
            onSelect={addItem.onSelect}
            testID={testID ? `${testID}-add` : undefined}>
            {addItem.label}
          </DropdownItem>
        ) : null}
        {listed.map((item) => (
          <DropdownItem
            // A checked radio also tints its row (a runtime NativeWind class):
            // remount on the change rather than restyle a live row mid-close.
            key={restyleKey(item.key, isCurrent(item.ref))}
            icon={DECK_MATERIAL_ICONS[item.kind]}
            rightControl={{ type: 'radio', checked: isCurrent(item.ref) }}
            disabled={!!item.pending}
            description={item.pending ? disabledHint : undefined}
            onSelect={() => onSelect(item.ref)}
            testID={testID ? `${testID}-${item.key}` : undefined}>
            {item.label}
          </DropdownItem>
        ))}
      </DropdownContent>
    </Dropdown>
  );
}
(DeckMaterialsSwitcherMenu as { displayName?: string }).displayName =
  'DeckMaterialsSwitcherMenu';
